import { useState, useEffect } from "react";
import { CheckCircle2, User } from "lucide-react";

interface LPSocialProofToastProps {
  projectName?: string;
}

const socialProofMessages = [
  { name: "Ramesh", location: "Uppal", action: "booked a site visit", icon: "📅" },
  { name: "Suresh", location: "LB Nagar", action: "just enquired", icon: "📞" },
  { name: "Priya", location: "Dilsukhnagar", action: "booked a plot", icon: "✅" },
  { name: "Venkat", location: "Secunderabad", action: "booked a site visit", icon: "📅" },
  { name: "Lakshmi", location: "Kukatpally", action: "visited the site", icon: "🏠" },
  { name: "Ravi", location: "Madhapur", action: "just enquired", icon: "📞" },
  { name: "Anjali", location: "Gachibowli", action: "booked a plot", icon: "✅" },
  { name: "Krishna", location: "Miyapur", action: "booked a site visit", icon: "📅" },
  { name: "Srinivas", location: "Begumpet", action: "invested today", icon: "💰" },
  { name: "Meena", location: "Ameerpet", action: "booked a site visit", icon: "📅" },
];

const plotAlerts = [
  "Only 6 east-facing plots left!",
  "3 corner plots available",
  "12 bookings this week!",
  "High demand in GPR Exotica!",
  "Premium plots selling fast!",
  "₹50,000 off - Limited time!",
];

const LPSocialProofToast = ({ projectName = "GPR Infra" }: LPSocialProofToastProps) => {
  const [currentMessage, setCurrentMessage] = useState<{ type: 'booking' | 'alert', data: any } | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let messageIndex = 0;
    let alertIndex = 0;
    let showBooking = true;

    const showNextMessage = () => {
      if (showBooking) {
        setCurrentMessage({ 
          type: 'booking', 
          data: socialProofMessages[messageIndex % socialProofMessages.length] 
        });
        messageIndex++;
      } else {
        setCurrentMessage({ 
          type: 'alert', 
          data: plotAlerts[alertIndex % plotAlerts.length] 
        });
        alertIndex++;
      }
      showBooking = !showBooking;
      setIsVisible(true);

      // Hide after 4 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 4000);
    };

    // Start after 8 seconds
    const initialTimer = setTimeout(() => {
      showNextMessage();
      
      // Show every 12 seconds after that
      const interval = setInterval(showNextMessage, 12000);
      return () => clearInterval(interval);
    }, 8000);

    return () => clearTimeout(initialTimer);
  }, []);

  if (!currentMessage || !isVisible) return null;

  return (
    <div className={`fixed bottom-20 md:bottom-6 left-4 z-40 transition-all duration-500 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}`}>
      <div className="bg-card rounded-xl shadow-2xl border border-border p-3 max-w-[280px] animate-slide-up">
        {currentMessage.type === 'booking' ? (
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
              <User className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {currentMessage.data.icon} {currentMessage.data.name} from {currentMessage.data.location}
              </p>
              <p className="text-xs text-muted-foreground">{currentMessage.data.action}</p>
              <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-success" />
                {Math.floor(Math.random() * 10) + 1} minutes ago
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-destructive to-accent flex items-center justify-center flex-shrink-0 animate-pulse">
              <span className="text-lg">🔥</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-destructive">{currentMessage.data}</p>
              <p className="text-[10px] text-muted-foreground">Limited availability</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LPSocialProofToast;
